import { roofCellAt, wallCellAt } from "../../building_levels"
import { BODY_GROUND_OFFSET } from "../../lpc/body_placement"
import { Coordinate, EntityDimensions, TileSize } from "../../models"
import { TileBlockingRect, blockingRectOverlaps } from "../../tile_blocking_geometry"
import type { TileMapMetaProvider } from "../../tile_map_meta_provider"
import { JUMP_CARRY, JUMP_LANDING_MS } from "../../jump"

export { WALL_TOP_FLOOR, WALL_TOP_FLOOR_TILE, isWallTopFloor } from "./wall_top_floor"

/** How far a jump reaches for a wall in front of it: the body's box, carried this far on, must meet the wall. */
export const MOUNT_REACH = 16

/** A running takeoff can reach a block anywhere along the distance its jump carries it. */
export const runningMountReach = (speed: number): number =>
    Math.max(MOUNT_REACH, speed * JUMP_CARRY * JUMP_LANDING_MS / 1000)

type Blocking = Pick<TileMapMetaProvider, "getTileBlockingProfile" | "isCollisionAt">
const FULL_CELL: TileBlockingRect[] = [{ x: 0, y: 0, width: TileSize, height: TileSize }]
// The player's collision box is centred on its location (the hip), not its soles (Entity.getBounds).
const HALF_W = EntityDimensions.player.w / 2, HALF_H = EntityDimensions.player.h / 2

/** Whether what stands on `cell` blocks a body box centred on `at`. */
const blocksAt = (map: Blocking, cell: Coordinate, at: Coordinate): boolean => {
    const rects = map.getTileBlockingProfile(cell.x, cell.y) ?? (map.isCollisionAt(cell.x, cell.y) ? FULL_CELL : [])
    const x1 = at.x - HALF_W - cell.x * TileSize, y1 = at.y - HALF_H - cell.y * TileSize
    return rects.some(rect => !rect.groundSupport && blockingRectOverlaps(x1, y1, x1 + 2 * HALF_W, y1 + 2 * HALF_H, rect))
}

/** The cells a body box centred on `at` overlaps. */
const cellsUnder = (at: Coordinate): Coordinate[] => {
    const cells: Coordinate[] = []
    for (let x = Math.floor((at.x - HALF_W) / TileSize); x <= Math.floor((at.x + HALF_W) / TileSize); x++)
        for (let y = Math.floor((at.y - HALF_H) / TileSize); y <= Math.floor((at.y + HALF_H) / TileSize); y++) cells.push({ x, y })
    return cells
}

/**
 * Whether a body could stand on the top of a wall at `cell` of `lower`, on the storey `upper` above it
 * (undefined: not raised yet). A built wall stands on the cell with no roof over it (a jump from inside
 * a house would go through its ceiling), and up there nothing does: no wall or other blocking piece, no
 * ladder or hatch.
 */
export const wallTopOpenAt = (lower: TileMapMetaProvider, upper: TileMapMetaProvider | undefined, cell: Coordinate): boolean => {
    if (cell.x < 0 || cell.y < 0 || cell.x >= lower.width() || cell.y >= lower.height()) return false
    if (!wallCellAt(lower, cell) || roofCellAt(lower, cell)) return false
    if (!upper) return true
    return !wallCellAt(upper, cell) && !upper.isCollisionAt(cell.x, cell.y) && !upper.isStairAt(cell.x, cell.y)
}

/**
 * Whether the wall at `cell` can be climbed -- jumped up onto -- by its tiles' metadata (TileMeta.climbable, a tileset
 * tile property): its own piece there, or its top drawn on the row above it. Only stone blocks say so.
 */
export const wallClimbableAt = (map: Pick<TileMapMetaProvider, "resolveTileMapFromLayerId" | "getMapDeltaAt" | "resolveTileMeta">,
    cell: Coordinate): boolean => {
    const sprite = map.resolveTileMapFromLayerId("sprite")
    return [cell.y, cell.y - 1].some(y => {
        const tile = sprite?.[cell.x]?.[y]
        if (!tile || tile === "0") return false
        const parent = map.getMapDeltaAt(cell.x, y, "sprite")?.context?.parent
        if (parent ? parent.x !== cell.x || parent.y !== cell.y : y !== cell.y) return false
        return !!map.resolveTileMeta(tile)?.climbable
    })
}

/**
 * The wall a jump from `location` heading along `heading` comes down on top of: one the body's box meets within
 * `reach` of it, if that wall can be climbed (wallClimbableAt) and its top is open (wallTopOpenAt) -- a wall with
 * another stacked on it is too tall to jump, so that is none too. Of the walls met, the one the SOLES are heading into: the box stands round the hip, 21.6px
 * above them, and going sideways along a platform it meets the row north of the feet too; the body lands where its
 * feet go.
 */
export const wallMountAhead = (lower: TileMapMetaProvider, upper: TileMapMetaProvider | undefined,
    location: Coordinate, heading: Coordinate, reach = MOUNT_REACH): Coordinate | undefined => {
    const length = Math.hypot(heading.x, heading.y)
    if (!(length > 0) || !Number.isFinite(location?.x) || !Number.isFinite(location?.y)) return undefined
    const ux = heading.x / length, uy = heading.y / length
    const soles = { x: location.x, y: location.y + BODY_GROUND_OFFSET }
    for (let step = 2; step <= reach; step += 2) {
        const at = { x: location.x + ux * step, y: location.y + uy * step }
        const met = cellsUnder(at).filter(cell => wallCellAt(lower, cell) && blocksAt(lower, cell, at))
        if (met.length) {
            // Where the feet are heading: past the body's half-width, as far on as the box has come.
            const foot = { x: soles.x + ux * (step + HALF_W), y: soles.y + uy * (step + HALF_W) }
            const ahead = { x: Math.floor(foot.x / TileSize), y: Math.floor(foot.y / TileSize) }
            const distance = (cell: Coordinate) => Math.hypot((cell.x + .5) * TileSize - foot.x, (cell.y + .5) * TileSize - foot.y)
            const target = wallCellAt(lower, ahead) ? ahead : met.sort((a, b) => distance(a) - distance(b))[0]
            return wallClimbableAt(lower, target) && wallTopOpenAt(lower, upper, target) ? target : undefined
        }
        // Anything else in the way (a tree, a chest) stops the reach short of any wall behind it.
        if (cellsUnder(at).some(cell => blocksAt(lower, cell, at))) return undefined
    }
    return undefined
}

/** How far inside the wall's cell a landing keeps the soles. */
const LANDING_MARGIN = 4

/**
 * Where the soles of a jump from `location` heading along `heading` come down on the top of the wall at `cell`: on the
 * line they travel, where it passes nearest the cell's centre. A jump taken off-centre lands as far off-centre, rather
 * than being pulled across onto the middle. wallTopLanding keeps the spot inside the cell.
 */
export const mountSoles = (location: Coordinate, heading: Coordinate, cell: Coordinate): Coordinate => {
    const centre = { x: (cell.x + .5) * TileSize, y: (cell.y + .5) * TileSize }
    const length = Math.hypot(heading.x, heading.y)
    if (!(length > 0)) return centre
    const ux = heading.x / length, uy = heading.y / length
    const soles = { x: location.x, y: location.y + BODY_GROUND_OFFSET }
    const along = (centre.x - soles.x) * ux + (centre.y - soles.y) * uy
    return { x: soles.x + ux * along, y: soles.y + uy * along }
}

/**
 * Where a body comes down on the top of the wall at `cell`: its soles on the cell (at `soles`, kept inside the cell,
 * or else the centre; or, where its box is not clear of whatever stands on the storey `upper` there, as little nearer
 * the centre as clears it, or else the nearest of a few spots round the centre), as a location (the hip). Undefined:
 * there is no room up there.
 */
export const wallTopLanding = (upper: Blocking | undefined, cell: Coordinate, soles?: Coordinate): Coordinate | undefined => {
    const inside = (value: number | undefined, from: number) => Number.isFinite(value)
        ? Math.min(TileSize - LANDING_MARGIN, Math.max(LANDING_MARGIN, value - from)) : 16
    const aim = { sx: inside(soles?.x, cell.x * TileSize), sy: inside(soles?.y, cell.y * TileSize) }
    // A pixel at a time from the spot aimed at to the centre, so a body that does not fit moves no farther than it must.
    const steps = Math.ceil(Math.hypot(aim.sx - 16, aim.sy - 16))
    const towardCentre = Array.from({ length: steps + 1 }, (_, i) =>
        ({ sx: aim.sx + (16 - aim.sx) * i / (steps || 1), sy: aim.sy + (16 - aim.sy) * i / (steps || 1) }))
    const offsets = [16, 10, 22, 6, 26]
    const candidates = [...towardCentre, ...offsets.flatMap(sy => offsets.slice(0, 3).map(sx => ({ sx, sy })))
        .sort((a, b) => Math.hypot(a.sx - aim.sx, a.sy - aim.sy) - Math.hypot(b.sx - aim.sx, b.sy - aim.sy))]
    for (const { sx, sy } of candidates) {
        const at = { x: cell.x * TileSize + sx, y: cell.y * TileSize + sy - BODY_GROUND_OFFSET }
        if (!upper || !cellsUnder(at).some(other => blocksAt(upper, other, at))) return at
    }
    return undefined
}
